import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext.jsx";
import { PageHeader } from "../../theme/components/index.js";
import { backendGet } from "../../services/dataService.js";
import { usePaymentMatchingData } from "../../hooks/usePaymentMatchingData.js";
import { SEARCH_WORD_KEY } from "../../components/tables/utils/tableUtils.js";
import { DeleteBookingModal } from "./components/DeleteBookingModal.jsx";
import ClaimInfoModal from "./components/ClaimInfoModal.jsx";
import OverpaymentDialog from "./components/OverpaymentDialog.jsx";
import PaymentMatchingClaimList from "./components/PaymentMatchingClaimList.jsx";
import PaymentMatchingClaimDetailsPanel from "./components/PaymentMatchingClaimDetailsPanel.jsx";
import PaymentMatchingPaymentsPanel from "./components/PaymentMatchingPaymentsPanel.jsx";

export default function PaymentMatchingPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setBreadcrumbs } = useBreadcrumb();

  const {
    paymentImport,
    payments,
    paymentMatchings,
    bookings,
    bookingIn,
    bookingOut,
    differenceAmount,
    loadingPayments,
    loadingMatchings,
    fetchAllInitialData,
    fetchBookings,
    deleteBooking,
    assignPaymentsToClaim,
    approveAllExactMatches,
  } = usePaymentMatchingData(id);

  // paginierte bzw gefilterte claims.
  const [claims, setClaims] = useState([]);
  // Claims die eine mögliche bzw. exakte übereinstimmung haben.
  const [matchedClaims, setMatchedClaims] = useState([]);

  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "all"
  );
  const [claimsSearchword, setclaimsSearchword] = useState(
    searchParams.get("search") || ""
  );
  const [paymentsSearchword, setPaymentsSearchword] = useState(
    searchParams.get("payment_search") || ""
  );

  const [matchFilter, setMatchFilter] = useState(
    searchParams.get("match") || "all"
  );

  const [selectedClaim, setSelectedClaim] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [additionalPayments, setAdditionalPayments] = useState([]);

  const [loadingClaims, setLoadingClaims] = useState(false);
  const [loadingMoreClaims, setLoadingMoreClaims] = useState(false);
  const [claimsPage, setClaimsPage] = useState(1);
  const [hasMoreClaims, setHasMoreClaims] = useState(true);
  const claimsListRef = useRef(null);

  const [isAssigning, setIsAssigning] = useState(false);
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);

  const [selectedBookingToDelete, setSelectedBookingToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuBookingId, setMenuBookingId] = useState(null);

  const [paymentFilter, setPaymentFilter] = useState("all");

  // Overpayment dialog state
  const [isOverpaymentDialogOpen, setIsOverpaymentDialogOpen] = useState(false);
  const [overpaymentAmount, setOverpaymentAmount] = useState(0);

  // Claim details dialog state
  const [isClaimDialogOpen, setIsClaimDialogOpen] = useState(false);
  const [detailedClaim, setDetailedClaim] = useState(null);
  const [detailedClaimBookings, setDetailedClaimBookings] = useState([]);
  const [loadingDetailedClaim, setLoadingDetailedClaim] = useState(false);

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: "saved", label: "Gespeichert" },
      { value: "processing", label: "In Bearbeitung" },
      { value: "pre_check", label: "In Prüfung" },
    ],
    []
  );

  const filteredPayments = useMemo(
    () =>
      payments.filter((payment) => {
        // Filter by assigned/unassigned status
        if (paymentFilter === "assigned" && !payment.booking_id) return false;
        if (paymentFilter === "unassigned" && payment.booking_id) return false;

        // Filter by search term
        if (paymentsSearchword) {
          const term = paymentsSearchword.toLowerCase();
          const matchesSearch =
            (payment.remitter || "").toLowerCase().includes(term) ||
            (payment.description || "").toLowerCase().includes(term) ||
            (payment.iban || "").toLowerCase().includes(term) ||
            String(payment.amount || "").includes(term);
          if (!matchesSearch) return false;
        }

        return true;
      }),
    [payments, paymentFilter, paymentsSearchword]
  );

  useEffect(() => {
    const params = {
      status: statusFilter !== "all" ? statusFilter : undefined,
      search: claimsSearchword || undefined,
      match: matchFilter !== "all" ? matchFilter : undefined,
      schadenId: searchParams.get("schadenId") || undefined,
    };

    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });

    const currentParams = Object.fromEntries(searchParams.entries());
    if (JSON.stringify(currentParams) !== JSON.stringify(params)) {
      setSearchParams(params, { replace: true });
    }
  }, [statusFilter, claimsSearchword, matchFilter, setSearchParams]);

  useEffect(() => {
    const schadenId = searchParams.get("schadenId");
    if (schadenId && claims.length > 0) {
      const found = claims.find((c) => String(c.id) === String(schadenId));
      if (found) {
        setSelectedClaim(found);
      }
    }
  }, [claims, searchParams]);

  useEffect(() => {
    fetchAllInitialData();
  }, [id]);

  useEffect(() => {
    if (selectedClaim) {
      fetchBookings(selectedClaim.id);
    }
  }, [selectedClaim?.id]);

  useEffect(() => {
    setClaimsPage(1);
    setHasMoreClaims(true);
    fetchClaims(1, false);
  }, [statusFilter]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setClaimsPage(1);
      setHasMoreClaims(true);
      fetchClaims(1, false);
    }, 300);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimsSearchword]);

  const handleClaimsScroll = useCallback(() => {
    const container = claimsListRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasMoreClaims && !loadingMoreClaims && !loadingClaims) {
      fetchClaims(claimsPage + 1, true);
    }
  }, [hasMoreClaims, loadingMoreClaims, loadingClaims, claimsPage]);

  useEffect(() => {
    if (!paymentImport) {
      setBreadcrumbs([
        { name: "Home", path: "/home" },
        { name: "Buchungsadministration", path: "/admin/buchungen" },
        { name: "Zahlungsimporte", path: "/admin/buchungen/imports" },
        {
          name: "Zahlungszuordnung",
          path: `/admin/buchungen/imports/${id}/payment-matching`,
        },
      ]);
    } else {
      setBreadcrumbs([
        { name: "Home", path: "/home" },
        { name: "Buchungsadministration", path: "/admin/buchungen" },
        { name: "Zahlungsimporte", path: "/admin/buchungen/imports" },
        {
          name: paymentImport.file_name,
          path: `/admin/buchungen/imports/${id}`,
        },
        {
          name: "Zahlungszuordnung",
          path: `/admin/buchungen/imports/${id}/payment-matching`,
        },
      ]);
    }
  }, [paymentImport, id, setBreadcrumbs]);

  // Lader der Claim die eine Mögliche bzw. Exakte übereinstimmung haben.
  useEffect(() => {
    fetchMatchedClaims(paymentMatchings);
  }, [paymentMatchings]);

  const serializeClaim = (rawClaim) => {
    const { formdata, ...rest } = rawClaim;
    const parsedFormdata = JSON.parse(formdata || "{}");
    const {
      schaden_kostenvoranschlag,
      schaden_rechnungsbetrag,
      gesamtschadenkosten,
    } = parsedFormdata;

    return {
      ...rest,
      schaden_kostenvoranschlag,
      schaden_rechnungsbetrag,
      gesamtschadenkosten,
      betrag:
        gesamtschadenkosten ||
        schaden_rechnungsbetrag ||
        schaden_kostenvoranschlag,
      betrag_label: gesamtschadenkosten
        ? "Gesamtschadenkosten"
        : schaden_rechnungsbetrag
          ? "Rechnungsbetrag"
          : "Kostenvoranschlag",
    };
  };

  const fetchClaims = async (page = 1, append = false) => {
    const limit = 8;

    try {
      let options = {
        params: {
          include: ["vertrag", "gesellschaft", "objekt"],
          page,
          limit,
          sort: '-updated_at'
        },
      };

      if (statusFilter !== "all" || claimsSearchword) {
        options.params.filter = {};
        if (statusFilter !== "all") {
          options.params.filter.status = statusFilter;
        }
        if (claimsSearchword) {
          options.params.filter[SEARCH_WORD_KEY] = claimsSearchword;
        }
      }

      await backendGet("data/schadens", {
        label: "Schaden",
        setIsLoading: append ? setLoadingMoreClaims : setLoadingClaims,
        options,
        enqueueSnackbar,
        setResult: (res) => {
          const newClaims = res?.data?.map(serializeClaim) || [];
          const meta = res?.meta;

          if (append) {
            setClaims((prev) => {
              const existingIds = new Set(prev.map((c) => c.id));
              const filteredNew = newClaims.filter(
                (c) => !existingIds.has(c.id)
              );
              return [...prev, ...filteredNew];
            });
          } else {
            setClaims(newClaims);
          }

          setHasMoreClaims(meta?.page < meta?.last_page);
          setClaimsPage(meta?.page || 1);
        },
      });

      // Füge die Claims mit den möglichen o. exakten Verbindung
      // auf der ersten Seite zu den Claims hinzu
      if (page === 1) {
        setClaims((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const filteredNew = matchedClaims.filter(
            (c) => !existingIds.has(c.id)
          );
          return [...prev, ...filteredNew];
        });
      }
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Schäden.", {
        variant: "error",
      });
    }
  };

  const fetchMatchedClaims = async () => {
    try {
      if (paymentMatchings?.length === 0) return;

      const ids = paymentMatchings.map((pm) => pm.schaden_id);

      let options = {
        params: {
          include: ["vertrag", "gesellschaft", "objekt"],
          filters: {
            id: ids,
          },
        },
      };

      await backendGet("data/schadens", {
        label: "Schaden",
        setIsLoading: setLoadingClaims,
        options,
        enqueueSnackbar,
        setResult: (res) => {
          const newClaims = res?.data?.map(serializeClaim) || [];
          setMatchedClaims((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const filteredNew = newClaims.filter((c) => !existingIds.has(c.id));
            return [...prev, ...filteredNew];
          });
        },
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Fehler beim Laden der Schäden.", {
        variant: "error",
      });
    }
  };

  const fetchDetailedClaim = async (claimId) => {
    if (!claimId) return;

    setLoadingDetailedClaim(true);
    try {
      // Fetch claim and bookings in parallel
      const [claimResponse, bookingsResponse] = await Promise.all([
        backendGet(`data/schadens/${claimId}`, {
          label: "Schadendetails",
          setIsLoading: () => {},
          enqueueSnackbar,
          options: {
            params: {
              include: ["vertrag", "gesellschaft", "objekt"],
            },
          },
        }),
        backendGet(`data/schadens/${claimId}/bookings`, {
          label: "Buchungen",
          setIsLoading: () => {},
          enqueueSnackbar,
          options: {
            params: {
              api_version: "v2",
            },
          },
        }),
      ]);

      const claimData = serializeClaim(claimResponse.data);
      setDetailedClaim(claimData);
      setDetailedClaimBookings(bookingsResponse?.data || []);
    } catch (error) {
      enqueueSnackbar("Fehler beim Laden der Schadendetails.", {
        variant: "error",
      });
    } finally {
      setLoadingDetailedClaim(false);
    }
  };

  const handleOpenClaimDialog = async (claim) => {
    setIsClaimDialogOpen(true);
    await fetchDetailedClaim(claim.id);
  };

  const handleMenuOpen = (event, bookingId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuBookingId(bookingId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuBookingId(null);
  };

  const handleOpenDeleteDialog = (booking) => {
    setSelectedBookingToDelete(booking);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteBooking = async () => {
    if (!selectedBookingToDelete) {
      return;
    }

    const success = await deleteBooking(selectedBookingToDelete.id, {
      setIsLoading: setIsDeletingBooking,
    });

    if (success) {
      setIsDeleteDialogOpen(false);
      setSelectedBookingToDelete(null);
    }
  };

  const matchPayments = (claim, withAdditionalPayments = true) => {
    if (!claim) {
      return { exact: [], possible: [], total: 0 };
    }

    const paymentMatches = paymentMatchings.filter(
      (pm) => pm.schaden_id === claim.id
    );

    const exactMatches = paymentMatches.filter((m) => m.status === "exact");
    const possibleMatches = paymentMatches.filter(
      (m) => m.status === "possible"
    );

    const allPossibleMatches = [
      ...possibleMatches,
      ...(withAdditionalPayments
        ? additionalPayments.filter(
            (p) =>
              p.schaden_id === claim.id &&
              !exactMatches.some((e) => e.id === p.id) &&
              !possibleMatches.some((pm) => pm.id === p.id)
          )
        : []),
    ];

    return {
      exact: exactMatches,
      possible: allPossibleMatches,
      total: exactMatches.length + allPossibleMatches.length,
    };
  };

  const getMatchFilterStatus = (claim) => {
    const matches = matchPayments(claim, false);
    if (matches.exact.length > 0) return "exact";
    if (matches.possible.length > 0) return "possible";
    return "none";
  };

  const filteredClaims = useMemo(
    () =>
      claims.filter((c) => {
        // Search and status filtering is done by the backend
        // Only filter locally by matchFilter (based on payment matchings)
        const matchesMatchFilter =
          matchFilter === "all" ||
          matchPayments(c)?.[matchFilter]?.length > 0 ||
          (matchFilter === "none" && matchPayments(c)?.total === 0);

        return matchesMatchFilter;
      }),
    [claims, matchFilter, paymentMatchings, additionalPayments]
  );

  const sortedFilteredClaims = useMemo(() => {
    const order = { exact: 0, possible: 1, none: 2 };

    return [...filteredClaims].sort((a, b) => {
      const statusA = getMatchFilterStatus(a);
      const statusB = getMatchFilterStatus(b);
      return order[statusA] - order[statusB];
    });
  }, [filteredClaims, paymentMatchings, additionalPayments]);

  const addPaymentToPossibleMatches = (payment) => {
    if (!selectedClaim || !payment) return;

    const existingMatches = paymentMatchings.filter(
      (pm) => pm.schaden_id === selectedClaim.id
    );

    if (existingMatches.some((pm) => pm.payment_id === payment.id)) {
      enqueueSnackbar("Zahlung ist bereits ausgewählt.", {
        variant: "info",
      });
      return;
    }

    const paymentMatching = {
      schaden_id: selectedClaim.id,
      payment_id: payment.id,
      payment,
      status: "possible",
    };

    if (!additionalPayments.some((pm) => pm.payment_id === payment.id)) {
      setAdditionalPayments((prev) => [...prev, paymentMatching]);
      setSelectedPayments((prev) => [...prev, payment.id]);
    }
  };

  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments((prev) =>
      prev.includes(paymentId)
        ? prev.filter((id) => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const getSelectedPaymentsTotal = () => {
    return selectedPayments.reduce((sum, paymentId) => {
      const payment =
        payments.find((p) => p.id === paymentId) ||
        paymentMatchings.find((pm) => pm.payment_id === paymentId)?.payment;
      return sum + (payment ? parseFloat(payment.amount || 0) : 0);
    }, 0);
  };

  const confirmSelectedPayments = async () => {
    if (!selectedClaim || selectedPayments.length === 0) return;

    const selectedTotal = getSelectedPaymentsTotal();
    const claimAmount = parseFloat(
      selectedClaim.auszahlungsbetrag ||
        selectedClaim.gesamtschadenkosten ||
        selectedClaim.schaden_rechnungsbetrag ||
        selectedClaim.schaden_kostenvoranschlag ||
        0
    );

    const existingInSum = bookingIn.reduce(
      (sum, b) => sum + parseFloat(b.amount || 0),
      0
    );

    const totalIn = existingInSum + selectedTotal;
    const overpayment = claimAmount > 0 ? totalIn - claimAmount : 0;

    if (overpayment > 0.01) {
      setOverpaymentAmount(overpayment);
      setIsOverpaymentDialogOpen(true);
      return;
    }

    await doAssignPayments(false);
  };

  const doAssignPayments = async (createRefund = false) => {
    if (!selectedClaim || selectedPayments.length === 0) return;

    await assignPaymentsToClaim(selectedClaim.id, selectedPayments, {
      createRefund,
    });

    setSelectedPayments([]);
    setAdditionalPayments([]);
    setIsOverpaymentDialogOpen(false);
    setOverpaymentAmount(0);

    if (selectedClaim) {
      fetchBookings(selectedClaim.id);
    }
  };

  const handleApproveAllExactMatches = async () => {
    const exactClaimIds = sortedFilteredClaims
      .filter((claim) => getMatchFilterStatus(claim) === "exact")
      .map((claim) => claim.id);

    if (exactClaimIds.length === 0) return;

    await approveAllExactMatches({
      setIsLoading: setIsAssigning,
      onSuccess: () => {
        if (selectedClaim) {
          fetchBookings(selectedClaim.id);
        }
      },
    });
  };

  return (
    <Box>
      <PageHeader title="Zahlungszuordnung" />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          height: "80vh",
        }}
      >
        {/* Left: Claims list */}
        <Box
          sx={{
            flex: "0 0 32%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PaymentMatchingClaimList
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            statusOptions={STATUS_OPTIONS}
            claimsSearchword={claimsSearchword}
            onClaimsSearchwordChange={setclaimsSearchword}
            matchFilter={matchFilter}
            onMatchFilterChange={setMatchFilter}
            claimsListRef={claimsListRef}
            onClaimsScroll={handleClaimsScroll}
            loadingClaims={loadingClaims}
            sortedFilteredClaims={sortedFilteredClaims}
            matchPayments={matchPayments}
            selectedClaim={selectedClaim}
            onSelectClaim={(claim) => {
              setSelectedClaim(claim);
              setAdditionalPayments([]);

              const exactPayments = paymentMatchings
                .filter(
                  (pm) => pm.schaden_id === claim.id && pm.status === "exact"
                )
                .map((pm) => pm.payment_id);

              setSelectedPayments(exactPayments);

              setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set("schadenId", claim.id);
                return params;
              });
            }}
            loadingMoreClaims={loadingMoreClaims}
            hasMoreClaims={hasMoreClaims}
            approveAllExactMatches={handleApproveAllExactMatches}
            isAssigning={isAssigning}
          />
        </Box>

        {/* Middle: Claim details */}
        <Box
          sx={{
            flex: "0 0 32%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PaymentMatchingClaimDetailsPanel
            selectedClaim={selectedClaim}
            bookings={bookings}
            bookingIn={bookingIn}
            bookingOut={bookingOut}
            differenceAmount={differenceAmount}
            matchPayments={matchPayments}
            selectedPayments={selectedPayments}
            togglePaymentSelection={togglePaymentSelection}
            confirmSelectedPayments={confirmSelectedPayments}
            loadingMatchings={loadingMatchings}
            handleOpenClaimDialog={handleOpenClaimDialog}
            menuAnchorEl={menuAnchorEl}
            menuBookingId={menuBookingId}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
            handleOpenDeleteDialog={handleOpenDeleteDialog}
          />
        </Box>
        {/* Right: Payments */}
        <Box
          sx={{
            flex: "0 0 36%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PaymentMatchingPaymentsPanel
            paymentImport={paymentImport}
            paymentsSearchword={paymentsSearchword}
            onPaymentsSearchwordChange={setPaymentsSearchword}
            paymentFilter={paymentFilter}
            onPaymentFilterChange={setPaymentFilter}
            filteredPayments={filteredPayments}
            loadingPayments={loadingPayments}
            selectedClaim={selectedClaim}
            addPaymentToPossibleMatches={addPaymentToPossibleMatches}
          />
        </Box>
      </Box>

      <DeleteBookingModal
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteBooking}
        isLoading={isDeletingBooking}
        bookingId={selectedBookingToDelete}
      />

      <ClaimInfoModal
        open={isClaimDialogOpen}
        onClose={() => setIsClaimDialogOpen(false)}
        claim={detailedClaim}
        claimBookings={detailedClaimBookings}
        isLoading={loadingDetailedClaim}
      />

      <OverpaymentDialog
        open={isOverpaymentDialogOpen}
        onClose={() => setIsOverpaymentDialogOpen(false)}
        overpaymentAmount={overpaymentAmount}
        onAssignWithRefund={() => doAssignPayments(true)}
        onAssignWithoutRefund={() => doAssignPayments(false)}
      />
    </Box>
  );
}
